import {
  type EditorElement,
  type EditorElementLayout,
  DATA,
} from '@wix/component-protocol/schema';
import type {
  ContainerLayout,
  DataItem,
} from '@wix/ambassador-devcenter-v1-component-type-data/types';
import {
  ContentResizeDirection,
  ResizeDirection,
} from '@wix/ambassador-devcenter-v1-component-type-data/types';

export function findFirstSimpleContainerDefinition(
  editorElement: EditorElement,
): [string, DataItem] | null {
  const entry = Object.entries(editorElement.data ?? {}).find(
    ([, item]) =>
      item?.dataType === DATA.DATA_TYPE.container &&
      item?.container?.containerType === 'simple',
  ) as [string, DataItem] | undefined;
  return entry ?? null;
}

const CONTENT_TO_RESIZE_DIRECTION: Partial<
  Record<ContentResizeDirection, ResizeDirection>
> = {
  [ContentResizeDirection.horizontal]: ResizeDirection.horizontal,
  [ContentResizeDirection.vertical]: ResizeDirection.vertical,
  [ContentResizeDirection.none]: ResizeDirection.none,
  // `horizontalAndVertical` must map to an explicit value: an undefined
  // resizeDirection means "no override" and the editor would fall back to the
  // base layout instead of resolving to 'all'.
  [ContentResizeDirection.horizontalAndVertical]:
    ResizeDirection.horizontalAndVertical,
};

/**
 * `ContainerLayout` and `EditorElementLayout` only share the deprecated
 * `resizeDirection` key, so spreading a container layout into an editor
 * element layout leaks `containerResizeDirection`/`width`/`height` as junk
 * keys and loses the resize direction. `width`/`height` are initial-size
 * hints for layout regeneration, not editor layout fields — dropped here.
 */
export function mapContainerLayoutToEditorElementLayout(
  containerLayout: ContainerLayout,
): EditorElementLayout {
  const resizeDirection =
    (containerLayout.containerResizeDirection &&
      CONTENT_TO_RESIZE_DIRECTION[containerLayout.containerResizeDirection]) ||
    containerLayout.resizeDirection;

  return resizeDirection &&
    resizeDirection !== ResizeDirection.UNKNOWN_ResizeDirection
    ? { resizeDirection }
    : {};
}

/**
 * Field-level merge: the overlay wins only for fields it explicitly sets;
 * `null` and UNKNOWN enum values count as unset and fall back to the base
 * (the container's own declared layout).
 */
export function mergeContainerLayouts(
  base: ContainerLayout | undefined,
  overlay: ContainerLayout | undefined,
): ContainerLayout | undefined {
  if (!overlay) {
    return base;
  }

  const definedOverlay = Object.fromEntries(
    Object.entries(overlay).filter(
      ([key, value]) =>
        value != null &&
        !(
          key === 'containerResizeDirection' &&
          value === ContentResizeDirection.UNKNOWN_ContentResizeDirection
        ) &&
        !(
          key === 'resizeDirection' &&
          value === ResizeDirection.UNKNOWN_ResizeDirection
        ),
    ),
  );

  return { ...base, ...definedOverlay };
}
